import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAllUsers, getUserByEmail, makeUserAdmin, deleteUserDocument, findOrphanedUsers, cleanupOrphanedUsers, searchUserAcrossAllCollections, checkEmailInFirebaseAuth, checkUserInFirestore } from '../utils/makeAdmin';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Header from '../components/Header';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email'); // 'email', 'name', 'userId'
  const [searchResult, setSearchResult] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [quickSearch, setQuickSearch] = useState('');
  const [orphanedUsers, setOrphanedUsers] = useState([]);
  const [showOrphanedUsers, setShowOrphanedUsers] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [crossCollectionResults, setCrossCollectionResults] = useState(null);
  const [firebaseAuthStatus, setFirebaseAuthStatus] = useState(null);
  const [searchingAcrossCollections, setSearchingAcrossCollections] = useState(false);
  const [firestoreCheckResult, setFirestoreCheckResult] = useState(null);
  const [checkingFirestore, setCheckingFirestore] = useState(false);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('🔍 AdminUsers Debug: Attempting to fetch users...');
      const userList = await listAllUsers();
      console.log('✅ AdminUsers Debug: Successfully fetched users:', userList.length);
      setUsers(userList);
    } catch (err) {
      console.error('❌ AdminUsers Debug: Error fetching users:', err);
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on active tab
  useEffect(() => {
    if (!users.length) return;

    let filtered = [];
    switch (activeTab) {
      case 'all':
        filtered = users;
        break;
      case 'hosts':
        filtered = users.filter(user => 
          user.role === 'host' || !user.role || user.role === 'venue'
        );
        break;
      case 'teams':
        filtered = users.filter(user => 
          user.role === 'team_captain' || user.role === 'team_member'
        );
        break;
      case 'venues':
        filtered = users.filter(user => user.role === 'venue');
        break;
      case 'admins':
        filtered = users.filter(user => user.role === 'admin');
        break;
      default:
        filtered = users;
    }

    // Apply quick search filter
    if (quickSearch.trim()) {
      const searchTerm = quickSearch.toLowerCase().trim();
      console.log('🔍 Quick search term:', searchTerm);
      
      filtered = filtered.filter(user => {
        const emailMatch = user.email?.toLowerCase().includes(searchTerm);
        const fullNameMatch = user.fullName?.toLowerCase().includes(searchTerm);
        const displayNameMatch = user.displayName?.toLowerCase().includes(searchTerm);
        const idMatch = user.id?.toLowerCase().includes(searchTerm);
        
        const isMatch = emailMatch || fullNameMatch || displayNameMatch || idMatch;
        
        if (isMatch) {
          console.log('✅ Match found:', user.email, user.fullName, user.id);
        }
        
        return isMatch;
      });
      
      console.log(`🔍 Quick search results: ${filtered.length} users found`);
    }

    setFilteredUsers(filtered);
  }, [users, activeTab, quickSearch]);

  const handleSearchUser = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setError('');
      let user;
      
      if (searchType === 'email') {
        // Try to find in current users first, then fetch from database
        user = users.find(u => u.email?.toLowerCase() === searchQuery.trim().toLowerCase());
        if (!user) {
          user = await getUserByEmail(searchQuery.trim());
        }
      } else if (searchType === 'name') {
        user = users.find(u => 
          u.fullName?.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
          u.displayName?.toLowerCase().includes(searchQuery.trim().toLowerCase())
        );
      } else if (searchType === 'userId') {
        user = users.find(u => u.id === searchQuery.trim());
      }
      
      if (user) {
        setSearchResult(user);
      } else {
        setSearchResult(null);
        setError(`No user found with ${searchType === 'email' ? 'email' : searchType === 'name' ? 'name' : 'user ID'}: ${searchQuery.trim()}`);
      }
    } catch (err) {
      setSearchResult(null);
      setError('Failed to search for user');
      console.error(err);
    }
  };

  const handleMakeAdmin = async (userId) => {
    if (!window.confirm('⚠️ Are you sure you want to make this user an admin?\n\nThis will give them full access to the admin panel and all system functions. This action cannot be easily undone.')) {
      return;
    }
    
    try {
      await makeUserAdmin(userId);
      await fetchUsers(); // Refresh the list
      alert('✅ User made admin successfully!');
    } catch (err) {
      setError('Failed to make user admin');
      console.error(err);
    }
  };

  const handleRevokeAdmin = async (userId) => {
    if (!window.confirm('⚠️ Are you sure you want to revoke admin privileges from this user?\n\nThis will remove their admin access and change their role back to Host. They will lose access to the admin panel and system functions.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Directly update the user document by ID
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: 'host',
        adminRevokedAt: new Date(),
        adminRevokedBy: 'admin_interface'
      });
      
      await fetchUsers(); // Refresh the list
      alert('✅ Admin privileges revoked successfully! User is now a Host.');
    } catch (err) {
      console.error('Error revoking admin privileges:', err);
      setError('Failed to revoke admin privileges');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    const roleNames = {
      'host': 'Host',
      'team_captain': 'Team Captain',
      'team_member': 'Team Member',
      'venue': 'Venue',
      'admin': 'Admin'
    };

    if (!window.confirm(`⚠️ Are you sure you want to change this user's role to ${roleNames[newRole]}?\n\nThis will modify their access permissions and capabilities.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Directly update the user document by ID
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        roleChangedAt: new Date(),
        roleChangedBy: 'admin_interface'
      });
      
      await fetchUsers(); // Refresh the list
      alert(`✅ User role changed to ${roleNames[newRole]} successfully!`);
    } catch (err) {
      console.error('Error changing user role:', err);
      setError('Failed to change user role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('⚠️ Are you sure you want to delete this user?\n\nThis will mark the user as deleted and remove their access to the system. Their data will be preserved for audit purposes but they will no longer be able to sign in.\n\nThis action cannot be easily undone.')) {
      return;
    }
    
    try {
      await deleteUserDocument(userId);
      await fetchUsers(); // Refresh the list
      alert('✅ User deleted successfully!');
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  };

  const handleFindOrphanedUsers = async () => {
    try {
      setError('');
      const result = await findOrphanedUsers();
      
      if (result.success) {
        setOrphanedUsers(result.orphanedUsers);
        setShowOrphanedUsers(true);
        if (result.orphanedUsers.length > 0) {
          alert(`Found ${result.orphanedUsers.length} orphaned user documents!`);
        } else {
          alert('No orphaned users found!');
        }
      } else {
        setError('Failed to find orphaned users: ' + result.error);
      }
    } catch (err) {
      setError('Failed to find orphaned users');
      console.error(err);
    }
  };

  const handleCheckUserInFirestore = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter an email address to check');
      return;
    }
    
    try {
      setError('');
      setCheckingFirestore(true);
      setFirestoreCheckResult(null);
      
      const email = searchQuery.trim();
      const result = await checkUserInFirestore(email);
      
      if (result.success) {
        setFirestoreCheckResult(result);
        if (result.found) {
          alert('User found in Firestore! This is likely causing the signup issue.');
        } else {
          alert('User not found in Firestore. Check Firebase Authentication manually.');
        }
      } else {
        setError('Failed to check user: ' + result.error);
      }
    } catch (err) {
      setError('Failed to check user');
      console.error(err);
    } finally {
      setCheckingFirestore(false);
    }
  };

  const handleSearchAcrossAllCollections = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter an email address to search');
      return;
    }
    
    try {
      setError('');
      setSearchingAcrossCollections(true);
      setCrossCollectionResults(null);
      setFirebaseAuthStatus(null);
      
      const email = searchQuery.trim();
      
      // Search across all collections
      const collectionResults = await searchUserAcrossAllCollections(email);
      
      // Check Firebase Auth status
      const authStatus = await checkEmailInFirebaseAuth(email);
      
      if (collectionResults.success) {
        setCrossCollectionResults(collectionResults.results);
      }
      
      setFirebaseAuthStatus(authStatus);
      
      if (collectionResults.success && collectionResults.results.totalResults > 0) {
        alert(`Found ${collectionResults.results.totalResults} references to this email across ${collectionResults.results.foundIn.length} collections!`);
      } else {
        alert('No references found in Firestore collections.');
      }
      
    } catch (err) {
      setError('Failed to search across collections');
      console.error(err);
    } finally {
      setSearchingAcrossCollections(false);
    }
  };

  const handleCleanupOrphanedUsers = async () => {
    if (!window.confirm(`⚠️ Are you sure you want to delete ${orphanedUsers.length} orphaned user documents?\n\nThis will permanently remove these documents from Firestore. This action cannot be undone.`)) {
      return;
    }
    
    try {
      setCleaningUp(true);
      const userIds = orphanedUsers.map(user => user.id);
      const result = await cleanupOrphanedUsers(userIds);
      
      if (result.success) {
        const successCount = result.results.filter(r => r.success).length;
        alert(`✅ Cleanup complete! ${successCount} of ${result.totalProcessed} orphaned users deleted.`);
        setOrphanedUsers([]);
        setShowOrphanedUsers(false);
        await fetchUsers(); // Refresh the main user list
      } else {
        setError('Failed to cleanup orphaned users: ' + result.error);
      }
    } catch (err) {
      setError('Failed to cleanup orphaned users');
      console.error(err);
    } finally {
      setCleaningUp(false);
    }
  };

  const handleSetCustomId = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      setError('User not found');
      return;
    }

    const isHost = user.role === 'host';
    const isTeam = user.role === 'team_captain' || user.role === 'team_member';
    
    if (!isHost && !isTeam) {
      alert('❌ Custom ID can only be set for hosts and teams.');
      return;
    }

    const idType = isHost ? 'Host ID' : 'League ID';
    const currentId = isHost ? user.hostId : user.leagueId;
    
    const customId = prompt(
      `Enter custom ${idType} number for ${user.fullName || user.email}:\n\n` +
      `Current ${idType}: ${currentId || 'Not assigned'}\n\n` +
      `Enter a number (e.g., 42, 100, 777):`,
      currentId || ''
    );

    if (!customId || customId.trim() === '') {
      return; // User cancelled
    }

    const numericId = customId.trim();
    
    // Validate that it's a positive number
    if (!/^\d+$/.test(numericId) || parseInt(numericId) <= 0) {
      alert('❌ Please enter a valid positive number (e.g., 42, 100, 777)');
      return;
    }

    if (!window.confirm(
      `⚠️ Are you sure you want to set ${idType} to #${numericId}?\n\n` +
      `This will override the current ${idType} and cannot be easily undone.\n\n` +
      `User: ${user.fullName || user.email}\n` +
      `New ${idType}: #${numericId}`
    )) {
      return;
    }

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        [`${isHost ? 'hostId' : 'leagueId'}`]: numericId
      });
      await fetchUsers(); // Refresh the list
      alert(`✅ ${idType} set to #${numericId} successfully!`);
    } catch (err) {
      setError(`Failed to set ${idType}`);
      console.error(err);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'admin': <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">👑 Admin</span>,
      'host': <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">🎮 Host</span>,
      'team_captain': <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">🏆 Team Captain</span>,
      'team_member': <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">👤 Team Member</span>,
      'venue': <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">🏢 Venue</span>
    };
    return badges[role] || <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">❓ Unknown</span>;
  };

  const canBeMadeAdmin = (user) => {
    // Only hosts can be made admin (not teams or venues)
    return user.role === 'host' || !user.role;
  };

  const getTabConfig = () => [
    { id: 'all', name: 'All Users', icon: '👥', count: users.length },
    { id: 'hosts', name: 'Hosts', icon: '🎤', count: users.filter(u => u.role === 'host' || !u.role || u.role === 'venue').length },
    { id: 'teams', name: 'Teams', icon: '🏆', count: users.filter(u => u.role === 'team_captain' || u.role === 'team_member').length },
    { id: 'venues', name: 'Venues', icon: '🏢', count: users.filter(u => u.role === 'venue').length },
    { id: 'admins', name: 'Admins', icon: '👑', count: users.filter(u => u.role === 'admin').length }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="p-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-800 text-green-300 px-4 py-2 rounded hover:bg-gray-700 mb-4"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-white">👥 User Management</h1>
          <p className="text-gray-300 mt-2">Manage users across different roles and permissions</p>
        </div>

        {/* Search Section */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white">🔍 Search User</h2>
            <button
              onClick={handleFindOrphanedUsers}
              className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700"
            >
              🔍 Find Orphaned Users
            </button>
          </div>
          
          <div className="flex gap-2">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="p-2 border rounded bg-gray-900 text-white border-gray-700 min-w-[120px]"
            >
              <option value="email">Email</option>
              <option value="name">Name</option>
              <option value="userId">User ID</option>
            </select>
            <input
              type={searchType === 'email' ? 'email' : 'text'}
              placeholder={
                searchType === 'email' ? 'Enter email address' :
                searchType === 'name' ? 'Enter full name' :
                'Enter user ID'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchUser()}
              className="flex-1 p-2 border rounded bg-gray-900 text-white border-gray-700"
            />
            <button
              onClick={handleSearchUser}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Search
            </button>
            <button
              onClick={handleSearchAcrossAllCollections}
              disabled={searchingAcrossCollections}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:bg-purple-400"
            >
              {searchingAcrossCollections ? 'Searching...' : '🔍 Deep Search'}
            </button>
            <button
              onClick={handleCheckUserInFirestore}
              disabled={checkingFirestore}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-green-400"
            >
              {checkingFirestore ? 'Checking...' : '🔍 Check Firestore'}
            </button>
            <button
              onClick={() => {
                setSearchQuery('');
                setSearchResult(null);
                setError('');
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Clear
            </button>
          </div>
          {searchResult && (
            <div className="mt-4 p-4 bg-gray-700 rounded text-white">
              <h3 className="font-semibold mb-2">Search Result:</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>Email:</strong> {searchResult.email}</div>
                <div><strong>Name:</strong> {searchResult.fullName || 'N/A'}</div>
                <div><strong>Role:</strong> {getRoleBadge(searchResult.role)}</div>
                <div><strong>User ID:</strong> <code className="bg-gray-600 px-1 rounded">{searchResult.id}</code></div>
              </div>
              <div className="mt-3 flex gap-2">
                {canBeMadeAdmin(searchResult) && searchResult.role !== 'admin' && (
                  <button
                    onClick={() => handleMakeAdmin(searchResult.id)}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    Make Admin
                  </button>
                )}
                {searchResult.role === 'admin' && (
                  <button
                    onClick={() => handleRevokeAdmin(searchResult.id)}
                    className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700"
                  >
                    Revoke Admin
                  </button>
                )}
                {/* Set Custom ID Button (only show for hosts and teams) */}
                {(searchResult.role === 'host' || searchResult.role === 'team_captain' || searchResult.role === 'team_member') && (
                  <button
                    onClick={() => handleSetCustomId(searchResult.id)}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    Set Custom {searchResult.role === 'host' ? 'Host' : 'League'} ID
                  </button>
                )}
                <button
                  onClick={() => handleDeleteUser(searchResult.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
          
          {/* Comprehensive Search Results */}
          {(crossCollectionResults || firebaseAuthStatus) && (
            <div className="mt-4 p-4 bg-gray-700 rounded text-white">
              <h3 className="font-semibold mb-2">🔍 Deep Search Results for: {searchQuery}</h3>
              
              {/* Firebase Auth Status */}
              {firebaseAuthStatus && (
                <div className="mb-4 p-3 bg-gray-600 rounded">
                  <h4 className="font-medium mb-2">Firebase Authentication Status:</h4>
                  <div className={`text-sm ${firebaseAuthStatus.exists ? 'text-red-400' : 'text-green-400'}`}>
                    {firebaseAuthStatus.message}
                  </div>
                </div>
              )}
              
              {/* Cross Collection Results */}
              {crossCollectionResults && crossCollectionResults.totalResults > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium">Found in Firestore Collections:</h4>
                  {crossCollectionResults.foundIn.map((collectionResult, index) => (
                    <div key={index} className="p-3 bg-gray-600 rounded">
                      <h5 className="font-medium text-yellow-400 mb-2">
                        📁 {collectionResult.collection} ({collectionResult.matches.length} matches)
                      </h5>
                      <div className="space-y-2">
                        {collectionResult.matches.map((match, matchIndex) => (
                          <div key={matchIndex} className="text-sm bg-gray-500 p-2 rounded">
                            <div><strong>Document ID:</strong> {match.id}</div>
                            <div><strong>Email Field:</strong> {match.data.email || match.data.userEmail || match.data.captainEmail || match.data.venueEmail || 'N/A'}</div>
                            <div><strong>Created:</strong> {match.data.createdAt?.toDate?.().toLocaleString?.() || 'Unknown'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {crossCollectionResults && crossCollectionResults.totalResults === 0 && (
                <div className="text-green-400 text-sm">
                  ✅ No references found in any Firestore collections
                </div>
              )}
            </div>
          )}
          
          {/* Firestore Check Results */}
          {firestoreCheckResult && (
            <div className="mt-4 p-4 bg-gray-700 rounded text-white">
              <h3 className="font-semibold mb-2">🔍 Firestore Check Results for: {searchQuery}</h3>
              
              {firestoreCheckResult.found ? (
                <div className="p-3 bg-red-600 rounded">
                  <h4 className="font-medium mb-2">❌ User Found in Firestore</h4>
                  <p className="text-sm mb-2">This user exists in the Firestore users collection and is likely causing the signup issue.</p>
                  <div className="text-sm">
                    <strong>User ID:</strong> {firestoreCheckResult.userData.id}<br/>
                    <strong>Name:</strong> {firestoreCheckResult.userData.fullName || 'N/A'}<br/>
                    <strong>Role:</strong> {firestoreCheckResult.userData.role || 'N/A'}
                  </div>
                  <p className="text-sm mt-2">💡 Recommendation: Delete this user document from Firestore to allow signup.</p>
                </div>
              ) : (
                <div className="p-3 bg-green-600 rounded">
                  <h4 className="font-medium mb-2">✅ User Not Found in Firestore</h4>
                  <p className="text-sm">The user is not in the Firestore users collection. Check Firebase Authentication manually.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Orphaned Users Section */}
        {showOrphanedUsers && (
          <div className="bg-orange-50 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold">⚠️ Orphaned Users Found ({orphanedUsers.length})</h3>
                <p className="text-sm">These users exist in Firestore but not in Firebase Auth</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCleanupOrphanedUsers}
                  disabled={cleaningUp}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:bg-red-400"
                >
                  {cleaningUp ? 'Cleaning...' : '🗑️ Clean Up All'}
                </button>
                <button
                  onClick={() => setShowOrphanedUsers(false)}
                  className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                >
                  Hide
                </button>
              </div>
            </div>
            
            {orphanedUsers.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1">Email</th>
                      <th className="text-left py-1">Name</th>
                      <th className="text-left py-1">User ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orphanedUsers.map(user => (
                      <tr key={user.id} className="border-b border-orange-200">
                        <td className="py-1">{user.email}</td>
                        <td className="py-1">{user.fullName || 'N/A'}</td>
                        <td className="py-1 font-mono text-xs">{user.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-gray-800 rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-700">
            {getTabConfig().map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-green-600 text-white border-b-2 border-green-400'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{tab.icon}</span>
                  <span>{tab.name}</span>
                  <span className="bg-gray-600 text-white px-2 py-1 rounded-full text-xs">
                    {tab.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {getTabConfig().find(tab => tab.id === activeTab)?.name} ({filteredUsers.length})
              </h2>
              <button
                onClick={fetchUsers}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Quick search by name, email, or user ID..."
                  value={quickSearch}
                  onChange={(e) => setQuickSearch(e.target.value)}
                  className={`w-full p-2 border rounded text-sm ${
                    quickSearch.trim() ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                  }`}
                />
                {quickSearch.trim() && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-500 text-xs">
                    🔍 {filteredUsers.length} results
                  </div>
                )}
              </div>
              {quickSearch && (
                <button
                  onClick={() => setQuickSearch('')}
                  className="bg-gray-500 text-white px-3 py-2 rounded text-sm hover:bg-gray-600"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400 mx-auto mb-2"></div>
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {quickSearch.trim() 
                ? `No users found matching "${quickSearch}" in this category.`
                : 'No users found in this category.'
              }
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Full Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold">Created At</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr
                      key={user.id}
                      className="border-t hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      <td className="px-4 py-3">{user.fullName || 'N/A'}</td>
                      <td className="px-4 py-3 font-mono text-sm">{user.email}</td>
                      <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {user.createdAt?.toDate?.().toLocaleString?.() || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Role Change Dropdown */}
                          <select
                            value={user.role || 'host'}
                            onChange={(e) => handleChangeRole(user.id, e.target.value)}
                            className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            <option value="host">Host</option>
                            <option value="team_captain">Team Captain</option>
                            <option value="team_member">Team Member</option>
                            <option value="venue">Venue</option>
                            {canBeMadeAdmin(user) && <option value="admin">Admin</option>}
                          </select>
                          
                          {/* Make Admin Button (only show if not already admin and can be made admin) */}
                          {canBeMadeAdmin(user) && user.role !== 'admin' && (
                            <button
                              onClick={() => handleMakeAdmin(user.id)}
                              className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700"
                              title="Make Admin"
                            >
                              👑
                            </button>
                          )}
                          
                          {/* Revoke Admin Button (only show if already admin) */}
                          {user.role === 'admin' && (
                            <button
                              onClick={() => handleRevokeAdmin(user.id)}
                              className="bg-orange-600 text-white px-2 py-1 rounded text-xs hover:bg-orange-700"
                              title="Revoke Admin"
                            >
                              ⚠️
                            </button>
                          )}
                          
                          {/* Set Custom ID Button (only show for hosts and teams) */}
                          {(user.role === 'host' || user.role === 'team_captain' || user.role === 'team_member') && (
                            <button
                              onClick={() => handleSetCustomId(user.id)}
                              className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700"
                              title={`Set Custom ${user.role === 'host' ? 'Host' : 'League'} ID`}
                            >
                              ✏️
                            </button>
                          )}
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                            title="Delete User"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">💡 User Management Guide:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-700 text-sm">
            <div>
              <h4 className="font-medium mb-1">Role Types:</h4>
              <ul className="space-y-1">
                <li>• <strong>Host:</strong> Can create and host games</li>
                <li>• <strong>Team Captain:</strong> Can manage team and participate</li>
                <li>• <strong>Team Member:</strong> Can participate in team games</li>
                <li>• <strong>Venue:</strong> Can host games at specific locations</li>
                <li>• <strong>Admin:</strong> Full system access and management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-1">Actions:</h4>
              <ul className="space-y-1">
                <li>• <strong>Click any row:</strong> Open detailed user management page</li>
                <li>• <strong>Role Dropdown:</strong> Change user role instantly</li>
                <li>• <strong>👑 Make Admin:</strong> Grant full admin privileges (Hosts only)</li>
                <li>• <strong>⚠️ Revoke Admin:</strong> Remove admin privileges (Admins only)</li>
                <li>• <strong>✏️ Set Custom ID:</strong> Assign specific Host/League ID numbers</li>
                <li>• <strong>🗑️ Delete:</strong> Mark user as deleted (data preserved)</li>
                <li>• <strong>Search:</strong> Find users by email, name, or user ID</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
